import { useCallback, useEffect, useMemo, useReducer } from 'react'
import { doc, onSnapshot } from 'firebase/firestore'
import { db } from '../config/firebase.js'

// State reducer for efficient state management and minimal re-renders
const deviceInfoReducer = (state, action) => {
  switch (action.type) {
    case 'LOADING':
      return { ...state, loading: true, error: null }
    case 'SUCCESS':
      return { deviceInfo: action.payload, loading: false, error: null }
    case 'ERROR':
      return { deviceInfo: null, loading: false, error: action.payload }
    case 'RESET':
      return { deviceInfo: null, loading: false, error: null }
    default:
      return state
  }
}

/**
 * Firebase v11+ Device Info Hook
 * 
 * React 19+ optimized real-time device information retrieval.
 * Efficiently manages device metadata from Firestore with minimal re-renders.
 * 
 * Features:
 * - useReducer for optimal state management
 * - Memoized return value for performance
 * - Automatic Firestore timestamp conversion
 * - Real-time updates with snapshot listeners
 * 
 * @param {string} deviceId - Device serial number/ID
 * @returns {Object} Device information state with loading/error states
 */
export function useDeviceInfo(deviceId) {
  const [state, dispatch] = useReducer(deviceInfoReducer, {
    deviceInfo: null,
    loading: false,
    error: null,
  })

  // Memoized snapshot handler to prevent re-renders
  const handleSnapshot = useCallback((doc) => {
    if (doc.exists()) {
      const data = doc.data()
      
      // Transform Firestore timestamps efficiently using nullish coalescing
      const deviceInfo = {
        ...data,
        lastUpdated: data.lastUpdated?.toDate() ?? null,
      }
      
      dispatch({ type: 'SUCCESS', payload: deviceInfo })
    } else {
      dispatch({ 
        type: 'ERROR', 
        payload: 'Device information not available. Device may not have uploaded info yet.',
      })
    }
  }, [])

  // Memoized error handler
  const handleError = useCallback((err) => {
    console.error('Device info listener error:', err)
    dispatch({ 
      type: 'ERROR', 
      payload: err.message || 'Failed to fetch device information',
    })
  }, [])

  // Effect for managing Firestore subscription
  useEffect(() => {
    if (!deviceId?.trim()) {
      dispatch({ type: 'RESET' })
      return
    }

    dispatch({ type: 'LOADING' })
    
    const deviceInfoRef = doc(db, 'deviceInfo', deviceId)
    const unsubscribe = onSnapshot(deviceInfoRef, handleSnapshot, handleError)
    
    // Cleanup function
    return () => {
      unsubscribe?.()
    }
  }, [deviceId, handleSnapshot, handleError])

  // Return memoized state to prevent unnecessary re-renders
  return useMemo(() => state, [state])
}
